import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trackImages } from "../assets";
import { stevenJ, drLouie, drGeralad, camila } from "../assets";
import { Sparkle } from "lucide-react";

const VISIBLE_ICONS = 5;
const CONTAINER_HEIGHT = 500;
const ICON_HEIGHT = CONTAINER_HEIGHT / VISIBLE_ICONS;
const PAUSE_DURATION = 4000;
const SCROLL_DELAY = 500;
const ANIMATION_DURATION = 0.35; // faster, snappy
const HOVER_SCALE = 1.12;
const ACTIVE_SCALE = 1.18;
const GLOW_SHADOW = '0 0 16px 4px #00bfae55';

const tracks = [
  {
    image: trackImages.aiOralDiagnostics,
    title: "AI in Oral Diagnostics",
    subtitle: "Revolutionizing Dental Care with Artificial Intelligence",
    description:
      "Leading voices in AI in Oral Diagnostics will present groundbreaking developments where artificial intelligence is revolutionizing the early detection and precise diagnosis of oral diseases. By integrating machine learning and advanced imaging techniques, AI is setting new standards in accuracy and efficiency, paving the way for personalized treatment strategies.",
  },
  {
    image: trackImages.oralPathophysiology,
    title: "Oral Pathophysiology & Treatment",
    subtitle: "Oral Pathophysiology Is Innovations in Treatment and Care",
    description:
      "Delving into Oral Pathophysiology & Treatment, specialists will discuss the complex mechanisms underlying oral diseases and novel therapeutic approaches designed to target these processes.",
  },
  {
    image: trackImages.oralHealth,
    title: "Oral Health And Systemic Health",
    subtitle: "Oral Health and Systemic Health: Bridging the Vital Connection",
    description:
      "Complementing this, the connection between Oral Health & Systemic Health will be thoroughly explored, emphasizing how conditions in the mouth can influence overall well-being and chronic systemic diseases.",
  },
  {
    image: trackImages.emergencyTechnologies,
    title: "Emerging Technologies",
    subtitle: "The Next Wave: Innovations in Emerging Technologies",
    description:
      "The conference will highlight Emerging Technologies that are transforming the practice of dentistry and oral medicine, from digital scanning and 3D printing to telehealth solutions. These innovations are not only improving clinical outcomes but also expanding access to care.",
  },
  {
    image: trackImages.publicHealth,
    title: "Public Health",
    subtitle: "Challenges and Innovations For Future of Public Health",
    description:
      "A significant focus will be placed on Public Health initiatives, especially concerning vulnerable populations such as the elderly. As the global population ages, ensuring the well-being of older adults becomes a vital priority for healthcare systems and policymakers worldwide. Public health efforts aimed at this demographic focus on promoting healthy aging, preventing disease, and improving access to quality care.",
  },
  {
    image: trackImages.geriatrics,
    title: "Comprehensive Approaches to Geriatric Health",
    subtitle: "Managing Oral Health in Aging Populations",
    description:
      "Experts in Geriatrics will share insights on managing oral health in aging populations, addressing unique challenges like medication effects and cognitive impairments.",
  },
  {
    image: trackImages.dentalMedicine,
    title: "Dental Medicine",
    subtitle: "Advancing Excellence in Dental Medicine",
    description:
      "Dental medicine focuses on the prevention, diagnosis, and treatment of oral diseases, promoting overall oral health and patient well-being. Through innovative techniques and evidence-based care, dental medicine bridges science and clinical practice to enhance smiles and quality of life.",
  },
  {
    image: trackImages.prosthodontics,
    title: "Prosthodontics",
    subtitle: "Prosthodontics: Restoring Smiles, Renewing Confidence",
    description:
      "Prosthodontics focuses on the design, creation, and fitting of artificial replacements for teeth and other parts of the mouth. This specialized field helps restore function, aesthetics, and oral health for patients with missing or damaged teeth, improving their quality of life through innovative dental solutions.",
  },
  {
    image: trackImages.maxillofacialSurgery,
    title: "Maxillofacial Surgery",
    subtitle: "Innovations in Maxillofacial Surgery",
    description:
      "Maxillofacial surgery is transforming patient care through innovative techniques that repair facial injuries, correct deformities, and restore oral function. Combining surgical expertise with modern technology, this specialty plays a vital role in enhancing both health and aesthetics.",
  },
  {
    image: trackImages.euroDentistry,
    title: "Euro Dentistry",
    subtitle: "Innovations and Trends Shaping European Dentistry",
    description:
      "Explore the latest innovations and emerging trends that are transforming dentistry across Europe. This dynamic field is driven by cutting-edge technologies, advanced treatment techniques, and a growing emphasis on patient-centered care. From digital dentistry and minimally invasive procedures to sustainable practices and cross-border collaborations, European dentistry is setting new standards in oral health.",
  },
  {
    image: trackImages.forensicOral,
    title: "Forensic Oral Medicine",
    subtitle: "Forensic Oral Medicine: Bridging Dentistry and Justice",
    description:
      "Forensic Oral Medicine is a vital discipline that utilizes dental knowledge to support legal processes. From identifying unknown victims to analyzing bite marks and age estimation, this field contributes essential evidence in criminal investigations and disaster scenarios. Discover how advances in forensic techniques are strengthening the role of oral medicine in upholding justice and solving complex cases.",
  },
  {
    image: trackImages.painManagement,
    title: "Therapeutics & Pain Management",
    subtitle: "Innovative Approaches to Therapeutics and Pain Relief",
    description:
      "Comprehensive sessions on Therapeutics & Pain Management and Pharmacology will provide critical updates on managing oral diseases and patient discomfort using advanced drug therapies, ensuring a holistic approach to patient care.",
  },
  {
    image: trackImages.pharmacology,
    title: "Pharmacology",
    subtitle: "Advances in Pharmacology: Enhancing Therapeutic Outcomes",
    description:
      "Pharmacology plays a critical role in modern medicine by exploring how drugs interact with the body to treat diseases effectively and safely. This field encompasses the development, testing, and application of medications that improve patient care across various medical specialties. Stay updated on the latest breakthroughs in drug research, personalized medicine, and innovative therapies that are revolutionizing treatment protocols and optimizing health outcomes worldwide.",
  },
  {
    image: trackImages.oralOncology,
    title: "Oral Oncology",
    subtitle: "New Innovations in Oncology Care",
    description:
      "In the realm of oncology, specialists will address the challenges of Oral Oncology, focusing on early detection, treatment options, and improving patient quality of life. Discussions will also cover Rare Diseases in Oral Medicine, highlighting diagnostic complexities and tailored treatment strategies for uncommon conditions.",
  },
  {
    image: trackImages.rareDiseaseOral,
    title: "Rare Diseases In Oral Medicine",
    subtitle: "Understanding Rare Diseases in Oral Medicine",
    description:
      "By exploring the latest research and treatment innovations, healthcare professionals can better diagnose these uncommon conditions and provide tailored care to improve patient outcomes. This specialized field addresses uncommon oral conditions that often require multidisciplinary care, advancing knowledge to improve outcomes and quality of life for affected individuals.",
  },
  {
    image: trackImages.remoteTreatment,
    title: "Remote Treatment",
    subtitle: "Remote Treatment: Expanding Access to Oral Healthcare",
    description:
      "Discover how remote treatment is transforming oral healthcare by enabling virtual consultations, diagnosis, and follow-ups. Utilizing telemedicine and digital tools, this approach improves accessibility for patients in remote or underserved areas, ensuring timely care and continuous support without the need for in-person visits.",
  },
  {
    image: trackImages.oralTuberculosis,
    title: "Oral Tuberculosis",
    subtitle: "Unveiling Oral Tuberculosis: Symptoms, Diagnosis, and Care",
    description:
      "Specialized topics such as Oral Tuberculosis will be addressed with the latest research findings and clinical protocols. Additionally, experts will examine the impact of Nutritional Deficiencies on oral health, underscoring the importance of integrated care that includes diet and systemic wellness.",
  },
  {
    image: trackImages.digitalDental,
    title: "Digital Dental Education",
    subtitle: "Revolutionizing Dental Training Through Digital Education",
    description:
      "Innovators in Digital Dental Education will demonstrate how virtual learning platforms and interactive technologies are reshaping dental education, ensuring future practitioners are well-prepared to meet evolving clinical demands.",
  },
  {
    image: trackImages.oralCancer,
    title: "Oral Cancer",
    subtitle: "Early Detection and Innovative Treatments For Oral Cancer",
    description:
      "Oral cancer remains a critical health concern worldwide. This session highlights the importance of early diagnosis, advances in treatment options, and preventive strategies. By understanding risk factors and leveraging cutting-edge research, healthcare professionals can improve survival rates and enhance the quality of life for patients affected by oral cancer.",
  },
  {
    image: trackImages.nutritionalDeficiency,
    title: "Nutritional Deficiencies",
    subtitle: "Impact of Nutritional Deficiencies on Oral Health",
    description:
      "Nutritional deficiencies play a crucial role in oral health, affecting tissue repair, immune response, and overall oral function. This topic explores how inadequate intake of essential vitamins and minerals can lead to various oral manifestations, and highlights strategies for prevention, diagnosis, and management to promote optimal dental wellness.",
  },
];

const ScrollingTracks = () => {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [pausedTrack, setPausedTrack] = useState(tracks[0]);
  const scrollRef = useRef(null);

  const loopedTracks = [...tracks, ...tracks];
  const originalLength = tracks.length;
  const middleOffset = originalLength;

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = middleOffset * ICON_HEIGHT;
    }
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const maxScroll = ICON_HEIGHT * originalLength * 2;
      if (container.scrollTop <= ICON_HEIGHT) {
        container.scrollTop += originalLength * ICON_HEIGHT;
      } else if (container.scrollTop >= maxScroll) {
        container.scrollTop -= originalLength * ICON_HEIGHT;
      }
      const newIndex = Math.round(container.scrollTop / ICON_HEIGHT) % originalLength;
      setIndex(newIndex);
      setPausedTrack(tracks[newIndex]);
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [tracks]);

  useEffect(() => {
    if (isHovered) return;
    const scrollTimeout = setTimeout(() => {
      const nextIndex = (index + 1) % tracks.length;
      setIndex(nextIndex);
      setPausedTrack(tracks[nextIndex]);
    }, SCROLL_DELAY + PAUSE_DURATION / 2); // faster
    return () => clearTimeout(scrollTimeout);
  }, [index, isHovered]);

  const scrollToIndex = (targetIndex) => {
    setIndex(targetIndex);
    setPausedTrack(tracks[targetIndex]);
    const container = scrollRef.current;
    if (container) {
      container.scrollTo({
        top: (middleOffset + targetIndex - 2) * ICON_HEIGHT,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="container flex w-full max-w-6xl h-[500px] overflow-hidden mx-auto relative px-4 scrollbar-hide">
      {/* Left: Smooth Scrolling Icons */}
      <div className="w-1/3 h-full overflow-y-auto relative scrollbar-hide">
        <motion.div
          className="flex flex-col will-change-transform"
          animate={{ y: -(index - 2) * ICON_HEIGHT }}
          transition={{
            ease: [0.33, 1, 0.68, 1], // clean ease
            duration: 0.6, // fast and sharp
          }}
        >
          {loopedTracks.map((track, i) => {
            const isActive = i % tracks.length === index;
            return (
              <motion.div
                key={`${track.title}-${i}`}
                className="flex w-40items-center justify-center gap-8 will-change-transform cursor-pointer scrollbar-hide"
                style={{ height: ICON_HEIGHT }}
                animate={{
                  opacity: isActive ? 1 : 0.55,
                  zIndex: isActive ? 10 : 1,
                  scale: isActive ? 1.06 : 0.74,
                }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 12,
                  mass: 0.2,
                }}
                onClick={() => {
                  const clickedIndex = i % tracks.length;
                  setIndex(clickedIndex);
                  setPausedTrack(tracks[clickedIndex]);
                }}
              >
                <img
                  src={track.image}
                  alt={track.title}
                  loading="lazy"
                  className="w-20 h-20 md:w-40 object-cover rounded-xl shadow-md mx-auto"
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Right: Info Panel */}
      <div className="w-92 h-full pl-6 flex items-center relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={pausedTrack.title}
            initial={{ opacity: 0, y: 20, scale: 0.97, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -20, scale: 0.97, filter: "blur(8px)" }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            className="bg-white border shadow-xl rounded-2xl p-6 max-w-4xl cursor-pointer"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <h2 className="text-3xl font-bold mb-1 text-one flex items-center gap-2">
              <Sparkle className="fill-one" />
              {pausedTrack.title}
            </h2>
            <h3 className="text-xl text-gray-600 font-semibold mb-3">
              {pausedTrack.subtitle}
            </h3>
            <p className="text-md text-gray-800 leading-relaxed">
              {pausedTrack.description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ScrollingTracks;
